/**
 * flagOps — the single source of truth for the manual "Mark Suspicious" / unflag
 * mutation (R-SSOT-7).
 *
 * Before this module the escalate/flag logic was forked across five surfaces:
 *   - Inspector ActionsTab "Flag"            (Workbench onMarkSuspicious)
 *   - Inspector ActionsTab "Unflag"          (Workbench onUnflag)
 *   - Inspector ActionsTab "Escalate"        (Workbench onEscalateToSuspicious)
 *   - right-click "Mark Suspicious"          (Workbench context menu)
 *   - Stash Tray "Promote to suspicious"     (StashTray confirmSuspicious)
 * Each carried its own copy of the FLAG_TAGS / FLAG_LABELS constants and behaved
 * subtly differently — the Inspector "Flag" stamped the *review* label
 * '🚩 Flagged' onto a `manually_flagged` tag (a latent mismatch), and the
 * right-click path skipped the flagService persist entirely. All five now call
 * here, so every surface produces the same canvas state and the same DB write.
 *
 * Canonical semantics of markSuspicious():
 *   1. clean slate — strip every existing flag tag + label, then add the single
 *      `manually_flagged` tag + '⚠ Suspicious' label (escalate behaviour applied
 *      universally — no [auto_flagged, manually_flagged] residue),
 *   2. raise risk_score to at least 0.85,
 *   3. mirror the patch onto the selected node when it is the same id, so the
 *      Inspector reflects it immediately,
 *   4. persist to the DB via flagService.create (best-effort). The §9j canvas
 *      flag SVG keys off the `manually_flagged` ENTITY TAG (not the label), so a
 *      failed persist still shows the flag — local canvas state is the source of
 *      truth for the badge.
 *
 * The mutation goes through useGraphStore.enrichElements (keyed merge), never a
 * full-array setElements scan.
 */
import { useGraphStore } from '../stores/useGraphStore';
import { useSelectionStore } from '../stores/useSelectionStore';
import { flagService } from './api';
import { detectChain } from '../utils/chainDetect';

/** Every tag the flag subsystem may have stamped (auto / manual / review). */
export const FLAG_TAGS = ['auto_flagged', 'manually_flagged', 'flagged_for_review'];
/** Decorative node_labels paired with the flag tags. */
export const FLAG_LABELS = ['🚩 Flagged', '🚩 Auto-flagged', '⚠ Suspicious'];

const SUSPICIOUS_TAG = 'manually_flagged';
const SUSPICIOUS_LABEL = '⚠ Suspicious';
const SUSPICIOUS_RISK = 0.85;

const asArr = (v: unknown): string[] => (Array.isArray(v) ? (v as string[]) : []);

export type FlagPersistResult = 'saved' | 'local';

/**
 * Mark a node as suspicious (manual verdict). Idempotent. Returns whether the DB
 * persist succeeded so the caller can tailor its toast.
 */
export async function markSuspicious(nodeId: string, chainHint?: string): Promise<FlagPersistResult> {
    const graph = useGraphStore.getState();
    const el = graph.elements.find(e => e.data?.id === nodeId && !e.data?.source);
    if (!el) return 'local';
    const cleanedTags = asArr(el.data.entity_tags).filter(t => !FLAG_TAGS.includes(t));
    const cleanedLabels = asArr(el.data.node_labels).filter(l => !FLAG_LABELS.includes(l));
    const patch = {
        flagged: true,
        risk_score: Math.max(Number(el.data.risk_score) || 0, SUSPICIOUS_RISK),
        entity_tags: [...cleanedTags, SUSPICIOUS_TAG],
        node_labels: [...cleanedLabels, SUSPICIOUS_LABEL],
    };
    graph.enrichElements([{ id: nodeId, data: patch }]);
    const sel = useSelectionStore.getState().selectedNode;
    if (sel?.id === nodeId) {
        useSelectionStore.getState().updateSelectedNode(patch as any);
    }
    try {
        await flagService.create(
            nodeId,
            chainHint || (el.data.chain as string) || detectChain(nodeId),
            'suspicious',
            undefined,
            SUSPICIOUS_RISK,
        );
        return 'saved';
    } catch (e) {
        console.error('[flagOps] markSuspicious persistence failed', e);
        return 'local';
    }
}

/**
 * Remove ALL flag tags + labels from a node (the Inspector "Unflag" — collapses
 * auto/manual/review into one toggle). For per-section dismiss (strip only one
 * kind) the Stash Tray keeps its own narrower helper.
 */
export async function unmarkAll(nodeId: string): Promise<FlagPersistResult> {
    const graph = useGraphStore.getState();
    const el = graph.elements.find(e => e.data?.id === nodeId && !e.data?.source);
    if (!el) return 'local';
    const patch = {
        flagged: false,
        entity_tags: asArr(el.data.entity_tags).filter(t => !FLAG_TAGS.includes(t)),
        node_labels: asArr(el.data.node_labels).filter(l => !FLAG_LABELS.includes(l)),
    };
    graph.enrichElements([{ id: nodeId, data: patch }]);
    const sel = useSelectionStore.getState().selectedNode;
    if (sel?.id === nodeId) {
        useSelectionStore.getState().updateSelectedNode(patch as any);
    }
    try {
        await flagService.remove(nodeId);
        return 'saved';
    } catch {
        return 'local';
    }
}
